import { useEffect, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import type { Schemas } from "@softmax/observatory-client/types";
import { requestUnavailable } from "@softmax/observatory-client/transport";
import { actualViewerQueryOptions } from "@softmax/web-features/session/records";
import { useResetSession } from "./use-reset-session";
import { viewerIdentity } from "./viewer-identity";

export function VerifiedViewer({
  viewer,
  children,
  fallback,
}: {
  readonly viewer: Schemas["WhoAmIResponse"];
  readonly children: ReactNode;
  readonly fallback?: ReactNode;
}) {
  const current = useQuery(actualViewerQueryOptions());
  const resetSession = useResetSession();
  const matches =
    current.data !== undefined &&
    viewerIdentity(current.data) === viewerIdentity(viewer);
  const rejected = requestUnavailable(current.error);
  useEffect(() => {
    if (current.isSuccess && !matches) resetSession();
  }, [current.isSuccess, matches, resetSession]);

  let content = fallback;

  if (matches && !rejected)
    content = (
      <div style={{ display: "contents" }} inert={current.isError}>
        {children}
      </div>
    );
  else if (content === undefined && !current.error)
    content = <output>Checking your access…</output>;

  return (
    <>
      {current.error && (
        <div role="alert">
          Your access could not be checked.{" "}
          <button onClick={() => void current.refetch()}>Try again</button>
        </div>
      )}
      {content}
    </>
  );
}
